// src/services/gen-app.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const genAppApi = createApi({
  reducerPath: "gen-app",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}/api/gen-app/`,
    prepareHeaders: (headers, { getState }) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    "Step1",
    "Step2",
    "Step3",
    "Step4",
    "Step5",
    "Step6",
    "Step7",
    "Step8",
    "Step9",
    "ActiveScholarships",
    "AppData",
  ],
  endpoints: (builder) => ({
    getStep1: builder.query({
      query: (appID) => `${appID}/step1`,
      providesTags: (result, error, appID) => [{ type: "Step1", id: appID }],
    }),
    saveStep1: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step1`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step1", id: appID },
      ],
    }),
    getStep2: builder.query({
      query: (appID) => `${appID}/step2`,
      providesTags: (result, error, appID) => [{ type: "Step2", id: appID }],
    }),
    saveStep2: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step2`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step2", id: appID },
        { type: "ActiveScholarships", id: appID },
      ],
    }),
    getAvailableAwards: builder.query({
      query: (appID) => `${appID}/available-awards`,
    }),
    getStep3: builder.query({
      query: (appID) => `${appID}/step3`,
      providesTags: (result, error, appID) => [{ type: "Step3", id: appID }],
    }),
    saveStep3: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step3`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step3", id: appID },
        { type: "ActiveScholarships", id: appID },
      ],
    }),
    getStep4: builder.query({
      query: (appID) => `${appID}/step4`,
      providesTags: (result, error, appID) => [{ type: "Step4", id: appID }],
    }),
    saveStep4: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step4`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step4", id: appID },
      ],
    }),
    getStep5: builder.query({
      query: (appID) => `${appID}/step5`,
      providesTags: (result, error, appID) => [{ type: "Step5", id: appID }],
    }),
    saveStep5: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step5`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step5", id: appID },
      ],
    }),
    getActiveScholarships: builder.query({
      query: (appID) => `active-scholarships?appID=${appID}`,
      providesTags: (result, error, appID) => [
        { type: "ActiveScholarships", id: appID },
      ],
    }),
    getStep6: builder.query({
      query: (appID) => `${appID}/step6`,
      providesTags: (result, error, appID) => [{ type: "Step6", id: appID }],
    }),
    saveStep6: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step6`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step6", id: appID },
      ],
    }),
    getStep7: builder.query({
      query: (appID) => `${appID}/step7`,
      providesTags: (result, error, appID) => [{ type: "Step7", id: appID }],
    }),
    saveStep7: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step7`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step7", id: appID },
      ],
    }),
    getStep8: builder.query({
      query: (appID) => `${appID}/step8`,
      providesTags: (result, error, appID) => [{ type: "Step8", id: appID }],
    }),
    saveStep8: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step8`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step8", id: appID },
        { type: "ActiveScholarships", id: appID },
      ],
    }),
    applyBursary: builder.mutation({
      query: ({ appID }) => ({
        url: `${appID}/apply-bursary`,
        method: "POST",
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step8", id: appID },
        { type: "ActiveScholarships", id: appID }, // recommended if this affects eligibility/list
      ],
    }),
    getStep9: builder.query({
      query: (appID) => `${appID}/step9`,
      providesTags: (result, error, appID) => [{ type: "Step9", id: appID }],
    }),
    saveStep9: builder.mutation({
      query: ({ appID, data }) => ({
        url: `${appID}/step9`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step9", id: appID },
        { type: "AppData", id: appID },
        { type: "Step1", id: appID },
        { type: "Step2", id: appID },
        { type: "Step3", id: appID },
        { type: "Step4", id: appID },
        { type: "Step5", id: appID },
        { type: "Step6", id: appID },
        { type: "Step7", id: appID },
        { type: "Step8", id: appID },
        { type: "ActiveScholarships", id: appID }, // optional but usually correct on submit
      ],
    }),
    getApplicationData: builder.query({
      query: (appID) => `${appID}/application-data`,
      providesTags: (result, error, appID) => [{ type: "AppData", id: appID }],
    }),
    sendPDF: builder.mutation({
      query: ({ appID, formData }) => ({
        url: `${appID}/send-pdf`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "AppData", id: appID },
      ],
    }),
    setBursaryOptOut: builder.mutation({
      query: ({ appID, bursaryOptOut }) => ({
        url: `${appID}/step8`,
        method: "POST",
        body: { bursaryOptOut },
      }),
      invalidatesTags: (result, error, { appID }) => [
        { type: "Step8", id: appID },
        { type: "ActiveScholarships", id: appID },
      ],
    }),
  }),
});

export const {
  useGetStep1Query,
  useSaveStep1Mutation,
  useGetStep2Query,
  useSaveStep2Mutation,
  useGetAvailableAwardsQuery,
  useGetStep3Query,
  useSaveStep3Mutation,
  useGetStep4Query,
  useSaveStep4Mutation,
  useGetStep5Query,
  useSaveStep5Mutation,
  useGetActiveScholarshipsQuery,
  useGetStep6Query,
  useSaveStep6Mutation,
  useGetStep7Query,
  useSaveStep7Mutation,
  useGetStep8Query,
  useSaveStep8Mutation,
  useApplyBursaryMutation,
  useGetStep9Query,
  useSaveStep9Mutation,
  useGetApplicationDataQuery,
  useSendPDFMutation,
  useSetBursaryOptOutMutation,
} = genAppApi;
export default genAppApi;
export { genAppApi };
